import { Injectable } from '@angular/core';
import { PullTestPiling } from 'shared_models';
import { StorageService } from '../storage.service';
import {
  OfflineActiveSession,
  OfflineDownloadProgress,
  OfflineMutation,
  OfflinePullTestMeta,
  OfflinePullTestSnapshot,
  OfflineTestDraft,
} from './offline-pull-test.types';

const ACTIVE_KEY = 'offline:active';

@Injectable({ providedIn: 'root' })
export class OfflinePullTestStoreService {
  /** Serializes queue read-modify-write per user+project to avoid dropped mutations. */
  private readonly queueWriteChains = new Map<string, Promise<void>>();

  constructor(private readonly storage: StorageService) {}

  private metaKey(userId: string, projectId: string): string {
    return `offline:meta:${userId}:${projectId}`;
  }

  private snapshotKey(userId: string, projectId: string): string {
    return `offline:snapshot:${userId}:${projectId}`;
  }

  private downloadKey(userId: string, projectId: string): string {
    return `offline:download:${userId}:${projectId}`;
  }

  private queueKey(userId: string, projectId: string): string {
    return `offline:queue:${userId}:${projectId}`;
  }

  private draftKey(userId: string, projectId: string, pullTestPilingId: string, testType: string): string {
    return `offline:draft:${userId}:${projectId}:${pullTestPilingId}:${testType}`;
  }

  private draftKeyPrefix(userId: string, projectId: string): string {
    return `offline:draft:${userId}:${projectId}:`;
  }

  private queueWriteChainKey(userId: string, projectId: string): string {
    return `${userId}:${projectId}`;
  }

  async getActiveSession(): Promise<OfflineActiveSession | null> {
    return this.storage.get<OfflineActiveSession>(ACTIVE_KEY);
  }

  async setActiveSession(session: OfflineActiveSession | null): Promise<void> {
    if (!session) {
      await this.storage.remove(ACTIVE_KEY);
      return;
    }
    await this.storage.set(ACTIVE_KEY, session);
  }

  async getMeta(userId: string, projectId: string): Promise<OfflinePullTestMeta | null> {
    return this.storage.get<OfflinePullTestMeta>(this.metaKey(userId, projectId));
  }

  async setMeta(userId: string, projectId: string, meta: OfflinePullTestMeta): Promise<void> {
    await this.storage.set(this.metaKey(userId, projectId), meta);
  }

  async getSnapshot(userId: string, projectId: string): Promise<OfflinePullTestSnapshot | null> {
    return this.storage.get<OfflinePullTestSnapshot>(this.snapshotKey(userId, projectId));
  }

  async setSnapshot(userId: string, projectId: string, snapshot: OfflinePullTestSnapshot): Promise<void> {
    await this.storage.set(this.snapshotKey(userId, projectId), snapshot);
  }

  async getDownloadProgress(userId: string, projectId: string): Promise<OfflineDownloadProgress | null> {
    return this.storage.get<OfflineDownloadProgress>(this.downloadKey(userId, projectId));
  }

  async setDownloadProgress(
    userId: string,
    projectId: string,
    progress: OfflineDownloadProgress
  ): Promise<void> {
    await this.storage.set(this.downloadKey(userId, projectId), progress);
  }

  async clearDownloadProgress(userId: string, projectId: string): Promise<void> {
    await this.storage.remove(this.downloadKey(userId, projectId));
  }

  /** Merge pilings by pullTestPilingID (used when resuming partial download). */
  mergePilings(existing: PullTestPiling[], incoming: PullTestPiling[]): PullTestPiling[] {
    const byId = new Map<string, PullTestPiling>();
    for (const p of existing) {
      byId.set(p.pullTestPilingID, p);
    }
    for (const p of incoming) {
      byId.set(p.pullTestPilingID, p);
    }
    return [...byId.values()];
  }

  async getQueue(userId: string, projectId: string): Promise<OfflineMutation[]> {
    return (await this.storage.get<OfflineMutation[]>(this.queueKey(userId, projectId))) ?? [];
  }

  async setQueue(userId: string, projectId: string, queue: OfflineMutation[]): Promise<void> {
    await this.storage.set(this.queueKey(userId, projectId), queue);
  }

  async appendMutation(userId: string, projectId: string, mutation: OfflineMutation): Promise<void> {
    const chainKey = this.queueWriteChainKey(userId, projectId);
    const append = async (): Promise<void> => {
      const queue = await this.getQueue(userId, projectId);
      queue.push(mutation);
      await this.setQueue(userId, projectId, queue);
    };
    const previous = this.queueWriteChains.get(chainKey) ?? Promise.resolve();
    const next = previous.then(append, append);
    this.queueWriteChains.set(chainKey, next);
    await next.finally(() => {
      if (this.queueWriteChains.get(chainKey) === next) {
        this.queueWriteChains.delete(chainKey);
      }
    });
  }

  async getTestDraft(
    userId: string,
    projectId: string,
    pullTestPilingId: string,
    testType: string
  ): Promise<OfflineTestDraft | null> {
    return this.storage.get<OfflineTestDraft>(
      this.draftKey(userId, projectId, pullTestPilingId, testType)
    );
  }

  async setTestDraft(
    userId: string,
    projectId: string,
    pullTestPilingId: string,
    testType: string,
    draft: OfflineTestDraft
  ): Promise<void> {
    await this.storage.set(this.draftKey(userId, projectId, pullTestPilingId, testType), draft);
  }

  async clearTestDraft(
    userId: string,
    projectId: string,
    pullTestPilingId: string,
    testType: string
  ): Promise<void> {
    await this.storage.remove(this.draftKey(userId, projectId, pullTestPilingId, testType));
  }

  /** Removes all in-progress test drafts for a project (called on successful offline exit). */
  async clearAllTestDrafts(userId: string, projectId: string): Promise<void> {
    const prefix = this.draftKeyPrefix(userId, projectId);
    const keys = await this.storage.listKeys();
    const draftKeys = keys.filter(key => key.startsWith(prefix));
    await Promise.all(draftKeys.map(key => this.storage.remove(key)));
  }

  async clearAllProjectData(userId: string, projectId: string): Promise<void> {
    await Promise.all([
      this.storage.remove(this.metaKey(userId, projectId)),
      this.storage.remove(this.snapshotKey(userId, projectId)),
      this.storage.remove(this.downloadKey(userId, projectId)),
      this.storage.remove(this.queueKey(userId, projectId)),
      this.clearAllTestDrafts(userId, projectId),
    ]);
  }
}
